import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useT } from '../../i18n';

export default function AdminRoles() {
  const t = useT();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [showPermForm, setShowPermForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', permission_ids: [] });
  const [permForm, setPermForm] = useState({ name: '', code: '', description: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    const [rRes, pRes] = await Promise.all([api.get('/roles/'), api.get('/permissions/')]);
    setRoles(rRes.data);
    setPermissions(pRes.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', description: '', permission_ids: [] });
    setError('');
    setShowRoleForm(true);
  };

  const openEditRole = (r) => {
    setEditingRole(r);
    setRoleForm({ name: r.name, description: r.description || '', permission_ids: r.permissions.map((p) => p.id) });
    setError('');
    setShowRoleForm(true);
  };

  const togglePerm = (id) => {
    setRoleForm((prev) => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(id)
        ? prev.permission_ids.filter((x) => x !== id)
        : [...prev.permission_ids, id],
    }));
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, roleForm);
      } else {
        await api.post('/roles/', roleForm);
      }
      setShowRoleForm(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('admin.common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePermSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/permissions/', permForm);
      setShowPermForm(false);
      setPermForm({ name: '', code: '', description: '' });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('admin.common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteRole = async (id) => {
    if (!window.confirm(t('admin.roles.confirmDelete'))) return;
    await api.delete(`/roles/${id}`);
    await load();
  };

  const deletePermission = async (id) => {
    if (!window.confirm(t('admin.roles.confirmDeletePermission'))) return;
    await api.delete(`/permissions/${id}`);
    await load();
  };

  return (
    <div>
      <h4 className="mb-4"><i className="bi bi-shield-check me-2" />{t('admin.roles.title')}</h4>

      <div className="row g-4">
        {/* Roles */}
        <div className="col-lg-7">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>{t('admin.roles.roles')}</h5>
            <button className="btn btn-sm btn-primary" onClick={openCreateRole}><i className="bi bi-plus me-1" />{t('admin.roles.new')}</button>
          </div>

          {showRoleForm && (
            <div className="card mb-3">
              <div className="card-body">
                <h6>{editingRole ? t('admin.roles.edit') : t('admin.roles.new')}</h6>
                <form onSubmit={handleRoleSubmit}>
                  <div className="row g-2 mb-2">
                    <div className="col-md-5">
                      <input className="form-control form-control-sm" placeholder={t('admin.roles.name')} required
                        value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} />
                    </div>
                    <div className="col-md-7">
                      <input className="form-control form-control-sm"                       placeholder={t('admin.roles.description')}
                        value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label small fw-bold">{t('admin.roles.permissions')}</label>
                    <div className="d-flex flex-wrap gap-2">
                      {permissions.map((p) => (
                        <div key={p.id} className="form-check form-check-inline">
                          <input className="form-check-input" type="checkbox" id={`p${p.id}`}
                            checked={roleForm.permission_ids.includes(p.id)}
                            onChange={() => togglePerm(p.id)} />
                          <label className="form-check-label small" htmlFor={`p${p.id}`}>{p.name}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {error && <div className="alert alert-danger py-1 small">{error}</div>}
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-sm btn-primary"                     disabled={submitting}>{t('admin.common.save')}</button>
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowRoleForm(false)}>{t('admin.common.cancel')}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {loading ? <div className="spinner-border" /> : (
            <div className="list-group">
              {roles.map((r) => (
                <div key={r.id} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{r.name}</strong>
                      {r.description && <div className="text-muted small">{r.description}</div>}
                      <div className="mt-1 d-flex flex-wrap gap-1">
                        {r.permissions.map((p) => (
                          <span key={p.id} className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle">{p.name}</span>
                        ))}
                      </div>
                    </div>
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-outline-primary" onClick={() => openEditRole(r)}><i className="bi bi-pencil" /></button>
                      <button className="btn btn-outline-danger" onClick={() => deleteRole(r.id)}><i className="bi bi-trash" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Permissions */}
        <div className="col-lg-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>{t('admin.roles.permissionsTitle')}</h5>
            <button className="btn btn-sm btn-outline-primary" onClick={() => setShowPermForm(!showPermForm)}><i className="bi bi-plus me-1" />{t('admin.roles.newPermission')}</button>
          </div>

          {showPermForm && (
            <div className="card mb-3">
              <div className="card-body">
                <form onSubmit={handlePermSubmit}>
                  <div className="mb-2">
                    <input className="form-control form-control-sm" placeholder={t('admin.roles.name')} required
                      value={permForm.name} onChange={(e) => setPermForm({ ...permForm, name: e.target.value })} />
                  </div>
                  <div className="mb-2">
                    <input className="form-control form-control-sm" placeholder={t('admin.roles.code')} required
                      value={permForm.code} onChange={(e) => setPermForm({ ...permForm, code: e.target.value.toLowerCase().replace(/\s/g, '_') })} />
                  </div>
                  <div className="mb-2">
                    <input className="form-control form-control-sm"                     placeholder={t('admin.roles.description')}
                      value={permForm.description} onChange={(e) => setPermForm({ ...permForm, description: e.target.value })} />
                  </div>
                  {error && <div className="alert alert-danger py-1 small">{error}</div>}
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-sm btn-primary"                     disabled={submitting}>{t('admin.roles.create')}</button>
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowPermForm(false)}>{t('admin.common.cancel')}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="list-group">
            {permissions.map((p) => (
              <div key={p.id} className="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <div className="fw-bold small">{p.name}</div>
                  <code className="text-muted" style={{ fontSize: '0.75rem' }}>{p.code}</code>
                </div>
                <button className="btn btn-sm btn-outline-danger" onClick={() => deletePermission(p.id)}><i className="bi bi-trash" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
